import { useState, useMemo } from 'react'
import {
  MessageSquareDot,
  Eye,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Search,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Textarea,
  Label,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useFeedbackList,
  useUpdateFeedbackStatus,
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  type Feedback as FeedbackType,
  type FeedbackFilters,
  type FeedbackCategory,
  type FeedbackStatus,
} from '../hooks/feedback/useFeedback'

const CATEGORY_STYLES: Record<FeedbackCategory, string> = {
  problem: 'bg-red-100 text-red-800',
  suggestion: 'bg-blue-100 text-blue-800',
  improvement: 'bg-green-100 text-green-800',
}

const STATUS_STYLES: Record<FeedbackStatus, string> = {
  new: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-800',
}

export function Feedback() {
  const [filters, setFilters] = useState<FeedbackFilters>({
    page: 1,
    limit: 20,
    sort: 'created_at:desc',
  })
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editStatus, setEditStatus] = useState('')
  const [editComment, setEditComment] = useState('')

  const { data, isLoading, error, refetch } = useFeedbackList(filters)
  const updateStatus = useUpdateFeedbackStatus()

  const handleFiltersChange = (newFilters: Partial<FeedbackFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const openDetail = (feedback: FeedbackType) => {
    setSelectedFeedback(feedback)
    setEditStatus(feedback.status)
    setEditComment(feedback.admin_comment || '')
    setDialogOpen(true)
  }

  const handleSaveStatus = async () => {
    if (!selectedFeedback) return
    try {
      await updateStatus.mutateAsync({
        id: selectedFeedback.id,
        data: {
          status: editStatus,
          admin_comment: editComment || undefined,
        },
      })
      setDialogOpen(false)
    } catch (err) {
      // error handled by mutation
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getEmployeeName = (feedback: FeedbackType) => {
    const emp = feedback.employees
    if (!emp) return '—'
    return [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean).join(' ')
  }

  const getEmployeeDepartment = (feedback: FeedbackType) => {
    return feedback.employees?.departments_employees_department_idTodepartments?.name || ''
  }

  // Statistics
  const stats = useMemo(() => {
    if (!data?.data) return null
    const items = data.data
    return {
      new: items.filter((f) => f.status === 'new').length,
      reviewed: items.filter((f) => f.status === 'reviewed').length,
      in_progress: items.filter((f) => f.status === 'in_progress').length,
      resolved: items.filter((f) => f.status === 'resolved').length,
    }
  }, [data])

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Обратная связь</h1>
          <p className="text-muted-foreground">Проблемы и предложения от сотрудников</p>
        </div>
        <ErrorState title="Ошибка загрузки обратной связи" onAction={refetch} />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Обратная связь</h1>
        <p className="text-muted-foreground">
          Просмотр и обработка проблем и предложений от сотрудников
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Фильтры</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Поиск по теме..."
                value={filters.search || ''}
                onChange={(e) => handleFiltersChange({ search: e.target.value || undefined })}
              />
            </div>

            <Select
              value={filters.category || ''}
              onChange={(e) =>
                handleFiltersChange({ category: e.target.value || undefined })
              }
            >
              <SelectOption value="">Все категории</SelectOption>
              <SelectOption value="problem">Проблема</SelectOption>
              <SelectOption value="suggestion">Предложение</SelectOption>
              <SelectOption value="improvement">Улучшение</SelectOption>
            </Select>

            <Select
              value={filters.status || ''}
              onChange={(e) =>
                handleFiltersChange({ status: e.target.value || undefined })
              }
            >
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="new">Новый</SelectOption>
              <SelectOption value="reviewed">Рассмотрен</SelectOption>
              <SelectOption value="in_progress">В работе</SelectOption>
              <SelectOption value="resolved">Решён</SelectOption>
              <SelectOption value="rejected">Отклонён</SelectOption>
            </Select>

            <Input
              type="date"
              value={filters.date_from?.split('T')[0] || ''}
              onChange={(e) =>
                handleFiltersChange({
                  date_from: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined,
                })
              }
              placeholder="Дата с"
            />

            <Input
              type="date"
              value={filters.date_to?.split('T')[0] || ''}
              onChange={(e) =>
                handleFiltersChange({
                  date_to: e.target.value ? `${e.target.value}T23:59:59.999Z` : undefined,
                })
              }
              placeholder="Дата по"
            />
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-yellow-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.new}</p>
                  <p className="text-sm text-muted-foreground">Новые</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Eye className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.reviewed}</p>
                  <p className="text-sm text-muted-foreground">Рассмотрены</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.in_progress}</p>
                  <p className="text-sm text-muted-foreground">В работе</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.resolved}</p>
                  <p className="text-sm text-muted-foreground">Решены</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Обращения</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} обращений`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data?.data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата</TableHead>
                    <TableHead>Сотрудник</TableHead>
                    <TableHead>Категория</TableHead>
                    <TableHead>Тема</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="w-[80px]">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(feedback.created_at)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{getEmployeeName(feedback)}</p>
                          {getEmployeeDepartment(feedback) && (
                            <p className="text-xs text-muted-foreground">
                              {getEmployeeDepartment(feedback)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORY_STYLES[feedback.category]}`}
                        >
                          {feedback.category === 'problem' && (
                            <AlertTriangle className="h-3 w-3 mr-1" />
                          )}
                          {feedback.category === 'suggestion' && (
                            <Lightbulb className="h-3 w-3 mr-1" />
                          )}
                          {feedback.category === 'improvement' && (
                            <TrendingUp className="h-3 w-3 mr-1" />
                          )}
                          {FEEDBACK_CATEGORY_LABELS[feedback.category]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="max-w-[300px] truncate">{feedback.title}</p>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[feedback.status]}`}
                        >
                          {FEEDBACK_STATUS_LABELS[feedback.status]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(feedback)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquareDot className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Обращения не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Пока нет обращений от сотрудников или измените фильтры.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedFeedback?.title}</DialogTitle>
            <DialogDescription>
              {selectedFeedback && (
                <span className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_STYLES[selectedFeedback.category]}`}
                  >
                    {FEEDBACK_CATEGORY_LABELS[selectedFeedback.category]}
                  </span>
                  <span className="text-xs">
                    {formatDate(selectedFeedback.created_at)}
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedFeedback && (
            <div className="space-y-4">
              {/* Employee info */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium">{getEmployeeName(selectedFeedback)}</p>
                {selectedFeedback.employees?.positions?.name && (
                  <p className="text-xs text-muted-foreground">
                    {selectedFeedback.employees.positions.name}
                  </p>
                )}
                {getEmployeeDepartment(selectedFeedback) && (
                  <p className="text-xs text-muted-foreground">
                    {getEmployeeDepartment(selectedFeedback)}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium">Описание</Label>
                <p className="mt-1 text-sm whitespace-pre-wrap">
                  {selectedFeedback.description}
                </p>
              </div>

              {/* Proposed solution */}
              {selectedFeedback.proposed_solution && (
                <div>
                  <Label className="text-sm font-medium">Предложенное решение</Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">
                    {selectedFeedback.proposed_solution}
                  </p>
                </div>
              )}

              <hr />

              {/* Admin actions */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Статус</Label>
                  <Select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="mt-1"
                  >
                    <SelectOption value="new">Новый</SelectOption>
                    <SelectOption value="reviewed">Рассмотрен</SelectOption>
                    <SelectOption value="in_progress">В работе</SelectOption>
                    <SelectOption value="resolved">Решён</SelectOption>
                    <SelectOption value="rejected">Отклонён</SelectOption>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">Комментарий администратора</Label>
                  <Textarea
                    value={editComment}
                    onChange={(e) => setEditComment(e.target.value)}
                    placeholder="Напишите комментарий..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSaveStatus}
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
